<template>
  <div class="card card-flush mb-10">
    <div class="card-header">
      <h3 class="card-title fw-bold">
        <i class="bi bi-shield-lock text-primary me-2"></i>
        Autenticação em dois fatores
      </h3>
      <div class="card-toolbar" v-if="status && !loading">
        <span class="badge" :class="status.enabled ? 'badge-light-success' : 'badge-light-warning'">
          {{ status.enabled ? 'Ativo' : 'Inativo' }}
        </span>
      </div>
    </div>
    <div class="card-body pt-5">
      <p class="text-muted mb-5" v-if="loading">Carregando...</p>
      <template v-else-if="status">
        <div class="two-factor-section__intro d-flex align-items-start gap-4 mb-6">
          <span class="two-factor-section__icon">
            <i class="bi bi-phone"></i>
          </span>
          <div>
            <p class="text-gray-800 fw-semibold mb-1">
              Proteja sua conta com um código temporário
            </p>
            <p class="text-muted mb-0 fs-7">
              Use um aplicativo autenticador (Google Authenticator, Microsoft Authenticator ou similar)
              para gerar códigos de acesso além da sua senha.
            </p>
          </div>
        </div>

        <div class="two-factor-section__status rounded border border-dashed p-5">
          <div class="d-flex flex-wrap align-items-center justify-content-between gap-4">
            <div>
              <span class="text-muted fs-7 fw-semibold text-uppercase d-block mb-2">Situação atual</span>
              <p class="mb-1 fw-bold text-gray-800">
                {{ status.enabled ? 'Verificação em duas etapas ativada' : 'Verificação em duas etapas desativada' }}
              </p>
              <p class="text-muted mb-0 fs-7" v-if="status.must_enforce">
                Obrigatório para sua conta — não pode ser desativado.
              </p>
              <p class="text-muted mb-0 fs-7" v-else-if="status.enabled">
                {{ status.recovery_codes_count }} código(s) de recuperação disponível(is)
              </p>
              <p class="text-muted mb-0 fs-7" v-else>
                Recomendamos ativar para aumentar a segurança do seu acesso.
              </p>
            </div>
            <div class="d-flex flex-wrap gap-2">
              <button type="button" class="btn btn-primary" v-if="!status.enabled" @click="startEnable">
                <i class="bi bi-shield-plus me-1"></i>
                Ativar 2FA
              </button>
              <button type="button" class="btn btn-light-primary" v-if="status.enabled" @click="regenerateCodes" :disabled="busy">
                Regenerar códigos
              </button>
              <button type="button" class="btn btn-light-danger" v-if="status.enabled && !status.must_enforce" @click="openDisableModal">
                Desativar
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <div class="modal fade" id="twoFactorEnableModal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content two-factor-enable">
        <div class="modal-header border-0 pb-0">
          <div>
            <h3 class="modal-title fw-bolder text-gray-900">Ativar autenticação em dois fatores</h3>
            <p class="text-muted mb-0 fs-7 mt-1">Configure seu aplicativo autenticador em poucos passos</p>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="px-9 pt-6">
          <div class="two-factor-enable__steps">
            <div class="two-factor-enable__step" :class="{ 'two-factor-enable__step--active': enableStep >= 1, 'two-factor-enable__step--done': enableStep > 1 }">
              <span class="two-factor-enable__step-number">1</span>
              <span class="two-factor-enable__step-label">Escanear</span>
            </div>
            <div class="two-factor-enable__step-line" :class="{ 'two-factor-enable__step-line--active': enableStep >= 2 }"></div>
            <div class="two-factor-enable__step" :class="{ 'two-factor-enable__step--active': enableStep >= 2, 'two-factor-enable__step--done': enableStep > 2 }">
              <span class="two-factor-enable__step-number">2</span>
              <span class="two-factor-enable__step-label">Confirmar</span>
            </div>
            <div class="two-factor-enable__step-line" :class="{ 'two-factor-enable__step-line--active': enableStep >= 3 }"></div>
            <div class="two-factor-enable__step" :class="{ 'two-factor-enable__step--active': enableStep >= 3 }">
              <span class="two-factor-enable__step-number">3</span>
              <span class="two-factor-enable__step-label">Recuperação</span>
            </div>
          </div>
        </div>

        <div class="modal-body px-9 pt-7 pb-9">
          <div v-if="enableStep === 0" class="two-factor-enable__loading text-center py-10">
            <span class="spinner-border text-primary mb-4"></span>
            <p class="text-gray-800 fw-semibold mb-1">Preparando QR Code</p>
            <p class="text-muted fs-7 mb-0">Aguarde enquanto geramos sua chave de autenticação...</p>
          </div>

          <template v-else-if="enableStep === 2">
            <div class="row g-8 align-items-center">
              <div class="col-md-6">
                <div class="two-factor-enable__instruction mb-5">
                  <span class="two-factor-enable__instruction-icon">
                    <i class="bi bi-qr-code-scan"></i>
                  </span>
                  <div>
                    <p class="fw-bold text-gray-900 mb-1">Abra seu autenticador</p>
                    <p class="text-muted fs-7 mb-0">
                      Escaneie o QR code ao lado ou adicione a conta manualmente no app.
                    </p>
                  </div>
                </div>

                <div class="two-factor-enable__apps d-flex flex-wrap gap-2 mb-6">
                  <span class="two-factor-enable__app-badge">
                    <i class="bi bi-google me-1"></i> Google Authenticator
                  </span>
                  <span class="two-factor-enable__app-badge">
                    <i class="bi bi-microsoft me-1"></i> Microsoft Authenticator
                  </span>
                </div>

                <label class="form-label fw-semibold required">Código de verificação</label>
                <input
                  v-model="confirmCode"
                  class="form-control form-control-lg form-control-solid two-factor-enable__code-input text-center"
                  maxlength="64"
                  autocomplete="one-time-code"
                  inputmode="numeric"
                  placeholder="000000"
                  @keyup.enter="confirmEnable"
                />
                <div class="form-text">Informe o código de 6 dígitos exibido no aplicativo.</div>
              </div>

              <div class="col-md-6">
                <div class="two-factor-enable__qr-card text-center">
                  <span class="text-muted fs-8 fw-semibold text-uppercase d-block mb-4">QR Code</span>
                  <div class="two-factor-enable__qr-frame mx-auto" v-html="qrSvg"></div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="enableStep === 3">
            <div class="alert alert-warning d-flex align-items-start gap-3 mb-6">
              <i class="bi bi-exclamation-triangle fs-2 text-warning"></i>
              <div>
                <p class="fw-bold mb-1">Guarde estes códigos agora</p>
                <p class="mb-0 fs-7">
                  Cada código pode ser usado uma única vez caso você perca acesso ao autenticador.
                  Salve em local seguro — eles não serão exibidos novamente.
                </p>
              </div>
            </div>

            <div class="two-factor-enable__codes-grid mb-6">
              <div
                v-for="(code, index) in confirmedRecoveryCodes"
                :key="index"
                class="two-factor-enable__code-chip"
              >
                {{ code }}
              </div>
            </div>

            <button type="button" class="btn btn-light-primary" @click="downloadCodes(confirmedRecoveryCodes)">
              <i class="bi bi-download me-1"></i>
              Baixar códigos
            </button>
          </template>
        </div>

        <div class="modal-footer border-0 pt-0 px-9 pb-9">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            {{ enableStep === 3 ? 'Concluir' : 'Cancelar' }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-if="enableStep === 2"
            :disabled="busy || !confirmCode.trim()"
            @click="confirmEnable"
          >
            <span v-if="!busy">Confirmar ativação</span>
            <span v-else>
              Confirmando...
              <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="twoFactorDisableModal" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Desativar 2FA</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <label class="form-label">Senha atual</label>
          <input v-model="disablePassword" type="password" class="form-control" />
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-danger" :disabled="busy" @click="disableTwoFactor">Desativar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Alert from '../helpers/alert';
import Modal from '../helpers/modal';
import { TwoFactorApi, downloadRecoveryCodes } from '../helpers/twoFactor';

export default {
  name: 'TwoFactorSection',
  data() {
    return {
      status: null,
      loading: true,
      busy: false,
      qrSvg: null,
      recoveryCodes: [],
      confirmedRecoveryCodes: [],
      confirmCode: '',
      disablePassword: '',
    };
  },
  computed: {
    enableStep() {
      if (this.confirmedRecoveryCodes.length) {
        return 3;
      }

      if (this.qrSvg) {
        return 2;
      }

      return 0;
    },
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    downloadCodes: downloadRecoveryCodes,
    loadStatus() {
      this.loading = true;
      TwoFactorApi.status()
        .then((response) => {
          this.status = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    startEnable() {
      this.qrSvg = null;
      this.recoveryCodes = [];
      this.confirmedRecoveryCodes = [];
      this.confirmCode = '';
      Modal.toggle('#twoFactorEnableModal');
      this.busy = true;

      TwoFactorApi.enable()
        .then((response) => {
          this.qrSvg = response.data.qr_code_svg;
          this.recoveryCodes = response.data.recovery_codes ?? [];
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? 'Não foi possível iniciar o 2FA.');
        })
        .finally(() => {
          this.busy = false;
        });
    },
    confirmEnable() {
      this.busy = true;
      TwoFactorApi.confirm(this.confirmCode.trim())
        .then((response) => {
          this.confirmedRecoveryCodes = response.data.recovery_codes ?? this.recoveryCodes;
          Alert.toastSuccess('2FA ativado com sucesso.');
          this.loadStatus();
        })
        .catch((error) => {
          const message = error.response?.data?.errors?.code?.[0]
            ?? error.response?.data?.message
            ?? 'Código inválido.';
          Alert.toastError(message);
        })
        .finally(() => {
          this.busy = false;
        });
    },
    regenerateCodes() {
      this.busy = true;
      TwoFactorApi.regenerateRecoveryCodes()
        .then((response) => {
          this.confirmedRecoveryCodes = response.data.recovery_codes ?? [];
          Modal.toggle('#twoFactorEnableModal');
          Alert.toastSuccess('Novos códigos gerados.');
          this.loadStatus();
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? 'Erro ao regenerar códigos.');
        })
        .finally(() => {
          this.busy = false;
        });
    },
    openDisableModal() {
      this.disablePassword = '';
      Modal.toggle('#twoFactorDisableModal');
    },
    disableTwoFactor() {
      this.busy = true;
      TwoFactorApi.disable(this.disablePassword)
        .then(() => {
          Alert.toastSuccess('2FA desativado.');
          this.disablePassword = '';
          Modal.hide('#twoFactorDisableModal');
          this.loadStatus();
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? 'Não foi possível desativar.');
        })
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>

<style scoped>
.two-factor-section__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: #f1faff;
  color: #009ef7;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.two-factor-section__status {
  background: #fafafa;
  border-color: #e4e6ef !important;
}

.two-factor-enable__steps {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.two-factor-enable__step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #a1a5b7;
  min-width: 0;
}

.two-factor-enable__step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: #f5f8fa;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.two-factor-enable__step-label {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.two-factor-enable__step--active {
  color: #181c32;
}

.two-factor-enable__step--active .two-factor-enable__step-number,
.two-factor-enable__step--done .two-factor-enable__step-number {
  background: #e8fff3;
  color: #50cd89;
}

.two-factor-enable__step-line {
  flex: 1;
  height: 2px;
  background: #eff2f5;
  min-width: 1rem;
}

.two-factor-enable__step-line--active {
  background: #50cd89;
}

.two-factor-enable__instruction {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.two-factor-enable__instruction-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: #f1faff;
  color: #009ef7;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.two-factor-enable__app-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  border-radius: 0.5rem;
  background: #f9f9f9;
  border: 1px solid #eff2f5;
  color: #5e6278;
  font-size: 0.8rem;
  font-weight: 600;
}

.two-factor-enable__code-input {
  letter-spacing: 0.35em;
  font-size: 1.35rem;
  font-weight: 700;
}

.two-factor-enable__qr-card {
  background: #fafafa;
  border: 1px dashed #e4e6ef;
  border-radius: 1rem;
  padding: 1.5rem;
}

.two-factor-enable__qr-frame {
  display: inline-flex;
  padding: 1rem;
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.04);
}

.two-factor-enable__qr-frame :deep(svg) {
  width: 180px !important;
  height: 180px !important;
}

.two-factor-enable__codes-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.two-factor-enable__code-chip {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: #f9f9f9;
  border: 1px solid #eff2f5;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9rem;
  font-weight: 600;
  color: #181c32;
  text-align: center;
}

@media (max-width: 767.98px) {
  .two-factor-enable__step-label {
    display: none;
  }

  .two-factor-enable__codes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
